import { AfterViewInit, Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, EMPTY } from "rxjs";
import { catchError, finalize, map, mergeMap, take, tap } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { IcOrganisation } from "@intocare-medapp/organisation/ic-organisation";
import { IcEnvironment } from "@intocare-medapp/environment";

import { CordovaService, SimpleToastService, SplashScreenService } from "../../shared/modules";
import { AuthModel, CheckClientService } from "app/shared";
import { EXPLICIT_SELECT_ORGANISATION } from "../constants";
import { PermissionDeniedError } from "app/shared/errors/permission-denied.error";
import { PasswordTooManyAttemptsError, UserDisabledError } from "@intocare-medapp/aws/http/entities";
import { injectQuery } from "@tanstack/angular-query-experimental";

@Component({
	selector: "ic-sign-in",
	templateUrl: "./sign-in.component.html",
	styleUrls: ["./sign-in.component.scss"],
	standalone: false,
})
export class SignInComponent implements OnInit, AfterViewInit {
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private splashscreenService = inject(SplashScreenService);
	private auth = inject(AuthModel);
	private translate = inject(TranslateService);
	private icOrganisation = inject(IcOrganisation);
	private toastService = inject(SimpleToastService);
	private translateService = inject(TranslateService);
	private icEnvironment = inject(IcEnvironment);
	private readonly checkClientService = inject(CheckClientService);

	private environment = this.icEnvironment.getEnvironment();
	appVersion = this.environment.APP_VERSION;

	orgName$ = this.icOrganisation.organisation$.pipe(map((organisation) => organisation.split(".").shift()));

	private loadingSubject$ = new BehaviorSubject(false);
	loading$ = this.loadingSubject$.asObservable();

	error$ = new BehaviorSubject(null);
	protected readonly clientCheckQuery = injectQuery(() => this.checkClientService.clientCheckQueryOptions());

	selectOrganisation() {
		this.error$.next(null);
		this.router.navigate(["select-organisation"], {
			state: {
				[EXPLICIT_SELECT_ORGANISATION]: true,
			},
		});
	}

	localLogin() {
		this.error$.next(null);
		this.router.navigate(["auth/sign-in-with-username"]);
	}

	login() {
		this.loadingSubject$.next(true);
		this.error$.next(null);
		return this.icOrganisation.organisation$
			.pipe(
				take(1),
				mergeMap((organisation) => {
					return this.auth.getLoginUri(organisation).pipe(
						mergeMap((loginUriData) => {
							console.info(`Login with [${organisation}]`, loginUriData);
							if (CordovaService.isBrowser()) {
								window.location.replace(loginUriData.url);
							} else {
								return AuthModel.handleInAppBrowserForAuth(loginUriData.url, loginUriData.stateRedirectUri, {
									closeButtonText: this.translate.instant("general.close"),
								}).pipe(
									mergeMap((code) => {
										if (!code || typeof code !== "string") {
											console.error(`Login failed - no code`, code);
											// @ts-expect-error TS2345
											this.error$.next("auth.errors.unknown");
											return EMPTY;
										}
										return this.auth.authenticateWithCode(code).pipe(
											tap(() => {
												this.router.navigate(["user-briefing"]);
											}),
											catchError((err) => {
												let message = "auth.errors.unknown";

												if (err instanceof PermissionDeniedError) {
													message = "auth.errors.permission_denied";
												}

												if (err instanceof PasswordTooManyAttemptsError) {
													message = "auth.errors.too_many_attempts";
												}

												if (err instanceof UserDisabledError) {
													message = "auth.errors.user_disabled";
												}

												this.toastService.error(this.translateService.instant(message));
												return EMPTY;
											}),
										);
									}),
								);
							}
							return EMPTY;
						}),
						catchError((err) => {
							console.error(`Login failed`, err);
							// @ts-expect-error TS2345
							this.error$.next("auth.errors.unknown");
							return EMPTY;
						}),
					);
				}),
				finalize(() => {
					this.loadingSubject$.next(false);
				}),
			)
			.subscribe();
	}

	ngAfterViewInit() {
		this.splashscreenService.hide();
	}

	ngOnInit(): void {
		const { code, state } = this.route.snapshot.queryParams;

		if (code && state) {
			this.loadingSubject$.next(true);

			const { organisation } = JSON.parse(atob(decodeURIComponent(state))) as { organisation: string };

			this.icOrganisation.setOrganisation(organisation);

			this.auth
				.authenticateWithCode(code)
				.pipe(
					tap(() => {
						this.router.navigate(["user-briefing"]);
					}),
					catchError((err) => {
						let message = "auth.errors.unknown";

						if (err instanceof PermissionDeniedError) {
							message = "auth.errors.permission_denied";
						}

						this.toastService.error(this.translateService.instant(message));
						return EMPTY;
					}),
					finalize(() => {
						this.loadingSubject$.next(false);
					}),
				)
				.subscribe();
		}
	}
}
